// MenuContent.jsx
import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  SpaceDashboardRounded as HomeIcon,
  SettingsRounded as SettingsIcon,
  LocalShippingRounded as LocalShippingIcon,
  ReceiptLongRounded as ReceiptIcon,
  AssignmentTurnedInRounded as AssignmentIcon,
  DirectionsCarFilledRounded as CarIcon,
  PaidRounded as MoneyIcon,
  InsertChartRounded as FreightReportIcon,
  SummarizeRounded as BillReportIcon,
  TravelExploreRounded as LandingIcon,
  SecurityRounded as SecurityIcon,
} from "@mui/icons-material";
import { usePermission } from "../../context/PermissionContext";

// ═══════════════════════════════════════════════════════════════
// Section colour system — each label gets its own identity
// ═══════════════════════════════════════════════════════════════
// Brand red — used for ALL selected states (matches reference image)
const BRAND = { main: "#DC2626", bg: "#FEF2F2" };

const SECTION = {
  dashboard: BRAND,
  settings: BRAND,
  vehicle: BRAND,
  expense: BRAND,
  freight: BRAND,
  billing: BRAND,
  issues: BRAND,
  freightReport: BRAND,
  billReport: BRAND,
  landing: BRAND,
  userAccess: BRAND,
};

const SUB_TEXT = "#64748B";

// ═══════════════════════════════════════════════════════════════
// PARENT ITEM STYLES
// ═══════════════════════════════════════════════════════════════
const buildItemStyles = (c) => ({
  borderRadius: "10px",
  marginY: "3px",
  mx: 1.25,
  backgroundColor: "transparent",
  color: "#374151",
  position: "relative",
  overflow: "hidden",
  transition: "all .2s ease",

  // Hover — soft red tint
  "&:hover": {
    backgroundColor: c.bg,
    color: c.main,
    "& .nav-icon-chip": { color: c.main },
    "& .MuiListItemText-primary": { color: c.main },
  },

  // Selected — SOLID RED background, WHITE text (like reference image)
  "&.Mui-selected": {
    backgroundColor: c.main,
    color: "#FFFFFF",
    fontWeight: 700,
    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.25)",
    "&:hover": {
      backgroundColor: c.main,
    },
    "& .nav-icon-chip": {
      color: "#FFFFFF",
    },
    "& .MuiListItemText-primary": {
      fontWeight: 700,
      color: "#FFFFFF",
    },
  },
});

// ═══════════════════════════════════════════════════════════════
// SUB ITEM STYLES — cleaner, no border conflicts
// ═══════════════════════════════════════════════════════════════
const buildSubItemStyles = (c) => ({
  borderRadius: "8px",
  marginY: "1px",
  mx: 1.25,
  pl: 5.5, // inset from left
  py: 0.6,
  minHeight: 34,
  backgroundColor: "transparent",
  color: SUB_TEXT,
  position: "relative",
  transition: "all .2s ease",

  // Bullet dot on the left
  "&::before": {
    content: '""',
    position: "absolute",
    left: 26,
    top: "50%",
    transform: "translateY(-50%)",
    width: 5,
    height: 5,
    borderRadius: "50%",
    backgroundColor: c.main,
    opacity: 0.4,
    transition: "all .2s ease",
  },

  "&:hover": {
    backgroundColor: c.bg,
    color: c.main,
    "&::before": {
      opacity: 1,
      transform: "translateY(-50%) scale(1.3)",
    },
    "& .MuiListItemText-primary": {
      color: c.main,
      fontWeight: 600,
    },
  },

  "&.Mui-selected": {
    backgroundColor: c.bg,
    color: c.main,
    "&:hover": { backgroundColor: c.bg },
    "&::before": {
      opacity: 1,
      transform: "translateY(-50%) scale(1.3)",
    },
    "& .MuiListItemText-primary": {
      color: c.main,
      fontWeight: 700,
    },
  },
});

const navIconStyles = {
  minWidth: 40,
  color: "#94A3B8",
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function MenuContent({ collapsed = false, onItemClick }) {
  const location = useLocation();
  const [activeSection, setActiveSection] = React.useState(null);

  // ─── RBAC ─────────────────────────────────────────────────────
  const { canAccess, isAdmin } = usePermission();

  const handleToggle = (section) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const isActive = (path) => location.pathname === path;
  const isSectionActive = (paths) =>
    paths.some((p) => location.pathname.includes(p));

  const handleMenuClick =
    (customOnClick, shouldCloseDrawer = false) =>
    (event) => {
      if (customOnClick) customOnClick(event);
      if (shouldCloseDrawer && onItemClick) onItemClick(event);
    };

  // ─── NAV ITEM ─────────────────────────────────────────────
  const NavItem = ({
    to,
    icon: Icon,
    label,
    sectionKey,
    onClick,
    selected,
    hasSubmenu,
    color,
  }) => {
    const c = color || SECTION.dashboard;
    const content = (
      <ListItemButton
        component={to ? Link : "div"}
        to={to}
        onClick={handleMenuClick(onClick, !!to && !hasSubmenu)}
        selected={selected}
        sx={{
          ...buildItemStyles(c),
          justifyContent: collapsed ? "center" : "flex-start",
          px: collapsed ? 1 : 1.5,
          py: 0.7,
          minHeight: 44,
        }}
      >
        <ListItemIcon sx={{ ...navIconStyles, minWidth: collapsed ? 0 : 36 }}>
          <Box
            className="nav-icon-chip"
            sx={{
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: selected ? c.main : "#64748B",
              transition: "color .2s ease",
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </ListItemIcon>

        {!collapsed && (
          <>
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: "0.86rem",
                fontWeight: selected ? 700 : 500,
              }}
            />
            {hasSubmenu && (
              <Box sx={{ display: "flex", color: "inherit" }}>
                {activeSection === sectionKey ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </Box>
            )}
          </>
        )}
      </ListItemButton>
    );

    if (collapsed) {
      return (
        <Tooltip title={label} placement="right" arrow>
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  // ─── SUB NAV ITEM ─────────────────────────────────────────
  const SubNavItem = ({ to, label, color }) => (
    <ListItemButton
      component={Link}
      to={to}
      onClick={onItemClick}
      selected={isActive(to)}
      sx={buildSubItemStyles(color || SECTION.dashboard)}
    >
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: "0.78rem",
          fontWeight: 500,
        }}
      />
    </ListItemButton>
  );

  // ─── Determine which sections/items to show ────────────────
  const showSettings =
    canAccess("stafflist") ||
    canAccess("partylist") ||
    canAccess("consignee-list");
  const showVehicle =
    canAccess("VehicleList") ||
    canAccess("VehicleFrom") ||
    canAccess("DocumentAlert");
  const showExpense = canAccess("ExpensesForm") || canAccess("ExpensesList");
  const showFreight =
    canAccess("freight-form") ||
    canAccess("freight-list") ||
    canAccess("freightinvoice") ||
    canAccess("freightpayment") ||
    canAccess("blank-lr")
  const showBilling = canAccess("bill-creation") || canAccess("bill-list") || canAccess("custom-bill-creation" || canAccess("custom-bill-list"));
  const showFreightReport =
    canAccess("Allfreight") ||
    canAccess("freightfilter") ||
    canAccess("Billpendingfreight") ||
    canAccess("Completefreight") ||
    canAccess("Partyfreight") ||
    canAccess("Vehiclefreight") ||
    canAccess("freight/payment/report") ||
    canAccess("expense-report");
  const showBillReport =
    canAccess("Allbills") ||
    canAccess("Completebill") ||
    canAccess("pendingforbill") ||
    canAccess("partyBill");
  const showLanding =
    canAccess("customer-form") ||
    canAccess("customer-list") ||
    canAccess("testimonial-form") ||
    canAccess("testimonial-list") ||
    canAccess("enquiry-details");

  return (
    <List dense sx={{ px: 0.5, py: 1 }}>
      {/* DASHBOARD */}
      {canAccess("dashboard") && (
        <NavItem
          to="/dashboard"
          icon={HomeIcon}
          label="Dashboard"
          color={SECTION.dashboard}
          selected={isActive("/dashboard")}
        />
      )}

      {/* SETTINGS */}
      {showSettings && (
        <>
          <NavItem
            icon={SettingsIcon}
            label="Settings"
            sectionKey="settings"
            hasSubmenu
            color={SECTION.settings}
            onClick={() => !collapsed && handleToggle("settings")}
            selected={isSectionActive([
              "/stafflist",
              "/consignee-list",
              "/partylist",
            ])}
          />
          <Collapse
            in={activeSection === "settings" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("stafflist") && (
                <SubNavItem
                  to="/dashboard/stafflist"
                  label="User"
                  color={SECTION.settings}
                />
              )}
              {/* USER ACCESS — Admin only */}
              {isAdmin && (
                <SubNavItem
                  to="/dashboard/user-access"
                  label="User Access"
                  color={SECTION.settings}
                />
              )}
              {canAccess("partylist") && (
                <SubNavItem
                  to="/dashboard/partylist"
                  label="Consignor"
                  color={SECTION.settings}
                />
              )}
              {canAccess("consignee-list") && (
                <SubNavItem
                  to="/dashboard/consignee-list"
                  label="Consignee"
                  color={SECTION.settings}
                />
              )}
              {canAccess("broker-list") && (
                <SubNavItem
                  to="/dashboard/broker-list"
                  label="Broker List"
                  color={SECTION.settings}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* MY VEHICLE */}
      {showVehicle && (
        <>
          <NavItem
            icon={CarIcon}
            label="My Vehicle"
            sectionKey="vehicle"
            hasSubmenu
            color={SECTION.vehicle}
            onClick={() => !collapsed && handleToggle("vehicle")}
            selected={isSectionActive([
              "/VehicleList",
              "/VehicleFrom",
              "/DocumentAlert",
            ])}
          />
          <Collapse
            in={activeSection === "vehicle" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("VehicleList") && (
                <SubNavItem
                  to="/dashboard/VehicleList"
                  label="Vehicle List"
                  color={SECTION.vehicle}
                />
              )}
              {canAccess("VehicleFrom") && (
                <SubNavItem
                  to="/dashboard/VehicleFrom"
                  label="Vehicle Form"
                  color={SECTION.vehicle}
                />
              )}
              {canAccess("DocumentAlert") && (
                <SubNavItem
                  to="/dashboard/DocumentAlert"
                  label="Document Alert"
                  color={SECTION.vehicle}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* VEHICLE EXPENSE */}
      {showExpense && (
        <>
          <NavItem
            icon={MoneyIcon}
            label="Vehicle Expense"
            sectionKey="expense"
            hasSubmenu
            color={SECTION.expense}
            onClick={() => !collapsed && handleToggle("expense")}
            selected={isSectionActive(["/ExpensesForm", "/ExpensesList"])}
          />
          <Collapse
            in={activeSection === "expense" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("ExpensesForm") && (
                <SubNavItem
                  to="/dashboard/ExpensesForm"
                  label="Expenses Form"
                  color={SECTION.expense}
                />
              )}
              {canAccess("ExpensesList") && (
                <SubNavItem
                  to="/dashboard/ExpensesList"
                  label="Expenses List"
                  color={SECTION.expense}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* LR */}
      {showFreight && (
        <>
          <NavItem
            icon={LocalShippingIcon}
            label="LR"
            sectionKey="freight"
            hasSubmenu
            color={SECTION.freight}
            onClick={() => !collapsed && handleToggle("freight")}
            selected={isSectionActive([
              "/freight-form",
              "/freight-list",
              "/due-lr",
              "/freightinvoice",
              "/not-received-freight",
              "/freightpayment",
              "/blank-lr"
            ])}
          />
          <Collapse
            in={activeSection === "freight" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("freight-form") && (
                <SubNavItem
                  to="/dashboard/freight-form"
                  label="LR Entry"
                  color={SECTION.freight}
                />
              )}
              {canAccess("freight-list") && (
                <SubNavItem
                  to="/dashboard/freight-list"
                  label="LR List"
                  color={SECTION.freight}
                />
              )}
              {canAccess("freightinvoice") && (
                <SubNavItem
                  to="/dashboard/freightinvoice"
                  label="Due LR"
                  color={SECTION.freight}
                />
              )}
              {canAccess("freightpayment") && (
                <SubNavItem
                  to="/dashboard/freightpayment"
                  label="PAYMENT AGS LR"
                  color={SECTION.freight}
                />
              )}
              {canAccess("blank-lr") && (
                <SubNavItem
                  to="/dashboard/blank-lr-list"
                  label="Blank LR List"
                  color={SECTION.freight}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* BILLING */}
      {showBilling && (
        <>
          <NavItem
            icon={ReceiptIcon}
            label="Billing"
            sectionKey="bill"
            hasSubmenu
            color={SECTION.billing}
            onClick={() => !collapsed && handleToggle("bill")}
            selected={isSectionActive(["/bill-creation", "/bill-list"])}
          />
          <Collapse
            in={activeSection === "bill" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("bill-creation") && (
                <SubNavItem
                  to="/dashboard/bill-creation"
                  label="Bill Creation"
                  color={SECTION.billing}
                />
              )}
              {canAccess("bill-list") && (
                <SubNavItem
                  to="/dashboard/bill-list"
                  label="Bill List"
                  color={SECTION.billing}
                />
              )}
               {canAccess("custom-bill-creation") && (
                <SubNavItem
                  to="/dashboard/custom-bill-creation"
                  label="Custom Bill Creation"
                  color={SECTION.billing}
                />
              )}
                {canAccess("custom-bill-list") && (
                <SubNavItem
                  to="/dashboard/custom-bill-list"
                  label="Custom Bill List"
                  color={SECTION.billing}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* ISSUES */}
      {canAccess("issue-list") && (
        <NavItem
          to="/dashboard/issue-list"
          icon={AssignmentIcon}
          label="Issues"
          color={SECTION.issues}
          selected={isActive("/dashboard/issue-list")}
        />
      )}

      {/* LR REPORTS */}
      {showFreightReport && (
        <>
          <NavItem
            icon={FreightReportIcon}
            label="LR Reports"
            sectionKey="freight report"
            hasSubmenu
            color={SECTION.freightReport}
            onClick={() => !collapsed && handleToggle("freight report")}
            selected={isSectionActive([
              "/Allfreight",
              "/freightfilter",
              "/Billpendingfreight",
              "/Completefreight",
              "/Partyfreight",
              "/Vehiclefreight",
              "/Branchfreight",
              "/freight/payment/report",
              "/expense-report",
            ])}
          />
          <Collapse
            in={activeSection === "freight report" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("Allfreight") && (
                <SubNavItem
                  to="/dashboard/Allfreight"
                  label="All LR"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("freightfilter") && (
                <SubNavItem
                  to="/dashboard/freightfilter"
                  label="LR Filter"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("Billpendingfreight") && (
                <SubNavItem
                  to="/dashboard/Billpendingfreight"
                  label="Bill Pending"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("Completefreight") && (
                <SubNavItem
                  to="/dashboard/Completefreight"
                  label="Complete LR"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("Partyfreight") && (
                <SubNavItem
                  to="/dashboard/Partyfreight"
                  label="Party LR"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("Vehiclefreight") && (
                <SubNavItem
                  to="/dashboard/Vehiclefreight"
                  label="Truck LR"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("freight/payment/report") && (
                <SubNavItem
                  to="/dashboard/freight/payment/report"
                  label="All LR Pay"
                  color={SECTION.freightReport}
                />
              )}
              {canAccess("expense-report") && (
                <SubNavItem
                  to="/dashboard/expense-report"
                  label="Expense Report"
                  color={SECTION.freightReport}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* BILL REPORTS */}
      {showBillReport && (
        <>
          <NavItem
            icon={BillReportIcon}
            label="Bill Reports"
            sectionKey="bill report"
            hasSubmenu
            color={SECTION.billReport}
            onClick={() => !collapsed && handleToggle("bill report")}
            selected={isSectionActive([
              "/Allbills",
              "/Completebill",
              "/pendingforbill",
              "/partyBill",
            ])}
          />
          <Collapse
            in={activeSection === "bill report" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("Allbills") && (
                <SubNavItem
                  to="/dashboard/Allbills"
                  label="All Bills"
                  color={SECTION.billReport}
                />
              )}
              {canAccess("Completebill") && (
                <SubNavItem
                  to="/dashboard/Completebill"
                  label="Complete Bills"
                  color={SECTION.billReport}
                />
              )}
              {canAccess("pendingforbill") && (
                <SubNavItem
                  to="/dashboard/pendingforbill"
                  label="Pending Bills"
                  color={SECTION.billReport}
                />
              )}
              {canAccess("partyBill") && (
                <SubNavItem
                  to="/dashboard/partyBill"
                  label="Party Bills"
                  color={SECTION.billReport}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* LANDING DATA */}
      {showLanding && (
        <>
          <NavItem
            icon={LandingIcon}
            label="Landing Data"
            sectionKey="landing data"
            hasSubmenu
            color={SECTION.landing}
            onClick={() => !collapsed && handleToggle("landing data")}
            selected={isSectionActive([
              "/customer-form",
              "/customer-list",
              "/testimonial-form",
              "/testimonial-list",
              "/enquiry-details",
            ])}
          />
          <Collapse
            in={activeSection === "landing data" && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {canAccess("customer-form") && (
                <SubNavItem
                  to="/dashboard/customer-form"
                  label="Customer Form"
                  color={SECTION.landing}
                />
              )}
              {canAccess("customer-list") && (
                <SubNavItem
                  to="/dashboard/customer-list"
                  label="Customer List"
                  color={SECTION.landing}
                />
              )}
              {canAccess("testimonial-form") && (
                <SubNavItem
                  to="/dashboard/testimonial-form"
                  label="Testimonial Form"
                  color={SECTION.landing}
                />
              )}
              {canAccess("testimonial-list") && (
                <SubNavItem
                  to="/dashboard/testimonial-list"
                  label="Testimonial List"
                  color={SECTION.landing}
                />
              )}
              {canAccess("enquiry-details") && (
                <SubNavItem
                  to="/dashboard/enquiry-details"
                  label="Enquiry Details"
                  color={SECTION.landing}
                />
              )}
            </List>
          </Collapse>
        </>
      )}
    </List>
  );
}
